'use client';

import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';

interface PlanFeature {
  text: string;
}

interface PricingPlan {
  name: string;
  price: string;
  priceNote: string;
  description: string;
  features: string[];
  recurring?: string;
  hourly?: string;
  highlight?: boolean;
}

interface PricingSection {
  label: string;
  title: string;
  plans: PricingPlan[];
}

export default function PricingPage() {
  const { t, language } = useLanguage();

  const websitePlans: PricingPlan[] = [
    {
      name: language === 'da' ? 'Starter Hjemmeside' : 'Starter Website',
      price: '8.995 DKK',
      priceNote: language === 'da' ? 'engangsopsætning' : 'one-time setup',
      description: language === 'da' ?'Perfekt til små virksomheder, der har brug for en professionel online tilstedeværelse.' :'Perfect for small businesses that need a professional online presence.',
      features: language === 'da'
        ? ['Brugerdefineret hjemmesidedesign', 'Mobilvenligt layout', 'Op til 5 sider', 'Kontaktformular', 'Grundlæggende SEO-opsætning']
        : ['Custom website design', 'Mobile-friendly layout', 'Up to 5 pages', 'Contact form', 'Basic SEO setup'],
      recurring: language === 'da' ? 'Hosting & vedligeholdelse: 349 DKK / måned' : 'Hosting & maintenance: 349 DKK / month',
      hourly: language === 'da' ? 'Hjemmesideopdateringer: 725 DKK / time' : 'Website updates: 725 DKK / hour',
    },
    {
      name: language === 'da' ? 'Erhvervshjemmeside' : 'Business Website',
      price: '14.995 DKK',
      priceNote: language === 'da' ? 'engangsopsætning' : 'one-time setup',
      description: language === 'da' ?'Til virksomheder, der har brug for flere sider og funktionalitet.' :'For companies that need more pages and functionality.',
      features: language === 'da'
        ? ['Brugerdefineret design tilpasset din virksomhed', 'Op til 10 sider', 'Avancerede formularer og indholdsafsnit', 'Performanceoptimering', 'Fuld hjemmesideudrulning']
        : ['Custom design tailored to your business', 'Up to 10 pages', 'Advanced forms and content sections', 'Performance optimization', 'Full website deployment'],
      recurring: language === 'da' ? 'Hosting & vedligeholdelse: 449 DKK / måned' : 'Hosting & maintenance: 449 DKK / month',
      hourly: language === 'da' ? 'Hjemmesideopdateringer: 725 DKK / time' : 'Website updates: 725 DKK / hour',
      highlight: true,
    },
    {
      name: language === 'da' ? 'Hjemmeside med Backend' : 'Website with Backend',
      price: '24.995 DKK',
      priceNote: language === 'da' ? 'engangsopsætning' : 'one-time setup',
      description: language === 'da' ?'Til virksomheder, der har brug for loginsystemer, databaser eller dynamisk funktionalitet.' :'For businesses that need login systems, databases, or dynamic functionality.',
      features: language === 'da'
        ? ['Brugerdefineret hjemmesidedesign', 'Backend-integration (Supabase eller lignende)', 'Databaseopsætning', 'Brugerlogin / beskyttet indhold', 'Dynamiske formularer og datahåndtering']
        : ['Custom website design', 'Backend integration (Supabase or similar)', 'Database setup', 'User login / protected content', 'Dynamic forms and data handling'],
      recurring: language === 'da' ? 'Hosting & vedligeholdelse: fra 599 DKK / måned' : 'Hosting & maintenance: from 599 DKK / month',
      hourly: language === 'da' ? 'Opdateringer & udvikling: 775 DKK / time' : 'Updates & development: 775 DKK / hour',
    },
    {
      name: language === 'da' ? 'Avanceret Hjemmeside med Backend' : 'Advanced Website with Backend',
      price: '39.995 DKK',
      priceNote: language === 'da' ? 'engangsopsætning' : 'one-time setup',
      description: language === 'da' ?'Til større platforme eller interne virksomhedsværktøjer.' :'For larger platforms or internal company tools.',
      features: language === 'da'
        ? ['Brugerdefineret design og udvikling', 'Backend-arkitektur', 'Admin-dashboard eller interne værktøjer', 'API-integrationer', 'Skalerbar infrastruktur']
        : ['Custom design and development', 'Backend architecture', 'Admin dashboard or internal tools', 'API integrations', 'Scalable infrastructure'],
      recurring: language === 'da' ? 'Hosting & vedligeholdelse: fra 799 DKK / måned' : 'Hosting & maintenance: from 799 DKK / month',
      hourly: language === 'da' ? 'Opdateringer & udvikling: 775 DKK / time' : 'Updates & development: 775 DKK / hour',
    },
  ];

  const appPlans: PricingPlan[] = [
    {
      name: language === 'da' ? 'Simpel App' : 'Simple App',
      price: '19.995 DKK',
      priceNote: '',
      description: language === 'da' ?'Lille mobilapplikation uden backend.' :'Small mobile application without backend.',
      features: language === 'da'
        ? ['2–3 skærme', 'Brugerdefineret UI-design', 'Grundlæggende funktionalitet', 'App-build og levering']
        : ['2–3 screens', 'Custom UI design', 'Basic functionality', 'App build and delivery'],
    },
    {
      name: language === 'da' ? 'Standard App' : 'Standard App',
      price: '34.995 DKK',
      priceNote: '',
      description: language === 'da' ?'Større applikation uden backend.' :'Larger application without backend.',
      features: language === 'da'
        ? ['Op til 10 skærme', 'Avanceret brugerflow', 'Brugerdefineret design', 'App-levering']
        : ['Up to 10 screens', 'Advanced user flow', 'Custom design', 'App delivery'],
      highlight: true,
    },
    {
      name: language === 'da' ? 'App med Backend' : 'App with Backend',
      price: '34.995 DKK',
      priceNote: '',
      description: language === 'da' ?'Til apps, der kræver brugerkonti, datalagring eller synkronisering.' :'For apps requiring user accounts, data storage, or sync.',
      features: language === 'da'
        ? ['2–3 skærme', 'Backend-integration', 'Databaseopsætning', 'Brugergodkendelse']
        : ['2–3 screens', 'Backend integration', 'Database setup', 'User authentication'],
    },
    {
      name: language === 'da' ? 'Erhvervsapp med Backend' : 'Business App with Backend',
      price: '59.995 DKK',
      priceNote: '',
      description: language === 'da' ?'Fuld forretningsapplikation med avanceret funktionalitet.' :'Full business application with advanced functionality.',
      features: language === 'da'
        ? ['Op til 10 skærme', 'Backend-infrastruktur', 'Brugerloginsystem', 'Datahåndtering']
        : ['Up to 10 screens', 'Backend infrastructure', 'User login system', 'Data management'],
      hourly: language === 'da' ? 'Yderligere udvikling: 775 DKK / time' : 'Additional development: 775 DKK / hour',
    },
  ];

  const itPlans: PricingPlan[] = [
    {
      name: language === 'da' ? 'Erhvervs-IT-support' : 'Business IT Support',
      price: '725 DKK',
      priceNote: language === 'da' ? '/ time' : '/ hour',
      description: language === 'da' ?'Professionel IT-support til virksomheder.' :'Professional IT support for companies.',
      features: language === 'da'
        ? ['Serveropsætning', 'Netværkskonfiguration', 'Systemadministration', 'Fejlfinding']
        : ['Server setup', 'Network configuration', 'System administration', 'Troubleshooting'],
    },
    {
      name: language === 'da' ? 'Privat IT-support' : 'Private IT Support',
      price: '495 DKK',
      priceNote: language === 'da' ? '/ time' : '/ hour',
      description: language === 'da' ?'IT-hjælp til privatpersoner og hjemmeopsætninger.' :'IT help for individuals and home setups.',
      features: language === 'da'
        ? ['Computer-fejlfinding', 'WiFi / hjemmenetværksopsætning', 'Softwareinstallation', 'Printer- og enhedsopsætning']
        : ['Computer troubleshooting', 'WiFi / home network setup', 'Software installation', 'Printer and device setup'],
      recurring: language === 'da' ? 'Minimum booking: 1 time' : 'Minimum booking: 1 hour',
      highlight: true,
    },
    {
      name: language === 'da' ? 'Større IT-projekter' : 'Larger IT Projects',
      price: language === 'da' ? 'Tilpasset tilbud' : 'Custom quote',
      priceNote: '',
      description: language === 'da' ?'Til infrastrukturopsætninger, migreringer eller fulde IT-miljøer.' :'For infrastructure setups, migrations, or full IT environments.',
      features: language === 'da'
        ? ['Infrastrukturopsætning', 'Systemmigrering', 'Fuld IT-miljøopsætning', 'Skræddersyet løsning']
        : ['Infrastructure setup', 'System migration', 'Full IT environment setup', 'Tailored solution'],
    },
  ];

  const serviceAreas = [
    { city: 'Vejle', services: ['IT hjælp', 'Computer hjælp', 'IT support privat', 'Hjemmeside'] },
    { city: 'Kolding', services: ['IT hjælp', 'Computer hjælp', 'IT support privat', 'Hjemmeside'] },
    { city: 'Fredericia', services: ['IT hjælp', 'Computer hjælp', 'IT support privat', 'Hjemmeside'] },
  ];

  return (
    <div className="min-h-screen bg-blackout text-monitor">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto relative z-10">
          <span className="inline-block text-xs font-black uppercase tracking-widest text-gold border border-gold/30 rounded-full px-4 py-1.5 mb-6 bg-gold/5">
            {language === 'da' ? 'Priser' : 'Pricing'}
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-monitor mb-6 leading-tight">
            {language === 'da' ? 'Digitale løsninger til din' : 'Digital solutions for your'}{' '} 
            <span className="text-gold">{language === 'da' ? 'virksomhed' : 'business'}</span>
          </h1>
          <p className="text-monitor/60 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {language === 'da' ?'Klare priser uden skjulte gebyrer. Vælg den løsning, der passer til din virksomhed.' :'Clear pricing with no hidden fees. Choose the solution that fits your business.'}
          </p>
        </div>
      </section>

      {/* Websites & Hosting */}
      <PricingSection
        label={language === 'da' ? '01' : '01'}
        title={language === 'da' ? 'Hjemmeside-udvikling' : 'Websites Development'}
        plans={websitePlans}
        language={language}
      />

      {/* App Development */}
      <PricingSection
        label="02"
        title={language === 'da' ? 'App-udvikling' : 'App Development'}
        plans={appPlans}
        language={language}
      />

      {/* IT Support */}
      <PricingSection
        label="03"
        title={language === 'da' ? 'IT-support & Infrastruktur' : 'IT Support & Infrastructure'}
        plans={itPlans}
        language={language}
      />

      {/* VAT Note */}
      <section className="py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="border border-gold/20 rounded-2xl bg-gold/5 px-6 py-5 flex items-start gap-3">
            <span className="text-gold text-lg mt-0.5">⚠</span>
            <p className="text-monitor/70 text-sm leading-relaxed">
              {language === 'da' ?'Alle priser er ekskl. moms. Større projekter kan kræve et tilpasset tilbud afhængigt af omfanget. Fakturering og administrativ afregning håndteres via ekstern samarbejdspartner.' :'All prices are excl. VAT. Larger projects may require a custom quote depending on scope. Billing and administrative settlement are handled via an external partner.'}
            </p>
          </div>
        </div>
      </section>

      {/* Local SEO / Service Areas */}
      <section className="py-16 px-4 sm:px-6" aria-label="Service areas">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-black uppercase tracking-widest text-gold border border-gold/30 rounded-full px-4 py-1.5 mb-4 bg-gold/5">
              {language === 'da' ? 'Serviceområder' : 'Service Areas'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-monitor mb-3">
              {language === 'da' ? 'Vi dækker hele Trekantområdet' : 'Covering the Triangle Region'}
            </h2>
            <p className="text-monitor/50 text-sm sm:text-base max-w-xl mx-auto">
              {language === 'da' ?'Nexavi leverer IT-hjælp, computer-support og hjemmesideudvikling i Vejle, Kolding og Fredericia — og omegn.' :'Nexavi delivers IT help, computer support, and website development in Vejle, Kolding, and Fredericia — and surrounding areas.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            {serviceAreas.map((area) => (
              <div
                key={area.city}
                className="border border-white/10 rounded-2xl bg-white/3 p-6 hover:border-gold/30 transition-all duration-300"
              >
                <h3 className="text-lg font-black text-gold mb-4">{area.city}</h3>
                <ul className="space-y-2">
                  {area.services.map((service) => (
                    <li key={service} className="flex items-center gap-2 text-sm text-monitor/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold/60 flex-shrink-0" />
                      {service} {area.city}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* SEO keyword paragraph — natural language, helps Google */}
          <div className="border border-white/5 rounded-2xl bg-white/2 p-6 sm:p-8">
            <p className="text-monitor/50 text-sm leading-relaxed text-center max-w-3xl mx-auto">
              {language === 'da' ?'Leder du efter IT hjælp i Vejle, Kolding eller Fredericia? Nexavi tilbyder professionel computer hjælp, IT support privat og erhvervs-IT i hele Trekantområdet. Vi hjælper med alt fra netværksopsætning og fejlfinding til hjemmesideudvikling og app-udvikling — hurtigt og til faste priser.' :'Looking for IT help in Vejle, Kolding, or Fredericia? Nexavi offers professional computer help, private IT support, and business IT services across the Triangle Region. We assist with everything from network setup and troubleshooting to website development and app development — fast and at fixed prices.'}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-monitor mb-4">
            {language === 'da' ? 'Klar til at komme i gang?' : 'Ready to get started?'}
          </h2>
          <p className="text-monitor/50 text-sm sm:text-base mb-8">
            {language === 'da' ?'Kontakt os i dag for et uforpligtende tilbud.' :'Contact us today for a no-obligation quote.'}
          </p>
          <a
            href="/home#contact"
            className="gold-shimmer-btn text-blackout text-sm font-black uppercase tracking-widest px-8 py-3 rounded-full transition-all hover:shadow-gold-glow inline-block"
          >
            {language === 'da' ? 'Kontakt os' : 'Get in Touch'}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

interface PricingSectionProps {
  label: string;
  title: string;
  plans: PricingPlan[];
  language: string;
}

function PricingSection({ label, title, plans, language }: PricingSectionProps) {
  return (
    <section className="py-16 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="flex items-center gap-4 mb-10">
          <span className="text-gold/30 font-black text-4xl sm:text-5xl leading-none select-none">{label}</span>
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-monitor">{title}</h2>
            <div className="h-px w-16 bg-gold/40 mt-2" />
          </div>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan) => (
            <PlanCard key={plan.name} plan={plan} language={language} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface PlanCardProps {
  plan: PricingPlan;
  language: string;
}

function PlanCard({ plan, language }: PlanCardProps) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${
        plan.highlight
          ? 'border-gold/50 bg-gold/5 shadow-gold-glow/20'
          : 'border-white/10 bg-white/3 hover:border-white/20'
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gold text-blackout text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap">
            {language === 'da' ? 'Populær' : 'Popular'}
          </span>
        </div>
      )}

      {/* Plan name */}
      <h3 className="text-sm font-black text-monitor uppercase tracking-widest mb-3">{plan.name}</h3>

      {/* Price */}
      <div className="mb-3">
        <span className="text-2xl sm:text-3xl font-black text-gold">{plan.price}</span>
        {plan.priceNote && (
          <span className="text-monitor/40 text-xs ml-2">— {plan.priceNote}</span>
        )}
      </div>

      {/* Description */}
      <p className="text-monitor/50 text-xs leading-relaxed mb-5">{plan.description}</p>

      {/* Divider */}
      <div className="h-px bg-white/10 mb-5" />

      {/* Features */}
      <ul className="space-y-2.5 flex-1">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-xs text-monitor/70">
            <span className="text-gold mt-0.5 flex-shrink-0">✓</span>
            {feature}
          </li>
        ))}
      </ul>

      {/* Recurring / hourly costs */}
      {(plan.recurring || plan.hourly) && (
        <div className="mt-5 pt-4 border-t border-white/10 space-y-1.5">
          {plan.hourly && (
            <p className="text-xs text-monitor/40 leading-snug">{plan.hourly}</p>
          )}
        </div>
      )}
    </div>
  );
}
